import {ShowMore, type ShowMoreRef, type ShowMoreToggleLinesFn} from '@re-dev/react-truncate'
import {announce} from '@react-aria/live-announcer'
import Big from 'big.js'

import CategoryDefault from '@/assets/food/icons/categoryDefault.svg?react'
import StoreDefault from '@/assets/food/icons/storeDefault.svg?react'
import CartPlusIcon from '@/assets/icons/cart-plus.svg?react'
import ChevronLeft from '@/assets/icons/chevron-left.svg?react'
import notFoundDecorSvg from '@/assets/images/not-found-page-decorator.svg'
import Loading from '@/components/Loading/Loading'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import type {FoodGrubhubCartOption} from '@/lib/food-grubhub/foodGrubhubCartDb'
import type {
  ChoiceCategoryList,
  ChoiceOptionList,
} from '@/lib/food-grubhub/foodRestaurantDetailModel'
import useFoodGrubhubCartMutation from '@/lib/food-grubhub/hooks/useFoodGrubhubCartMutation'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import useGrubhubMenuItemDetail from '@/lib/food-grubhub/hooks/useGrubhubMenuItemDetail'
import useRestaurantInformationDetail from '@/lib/food-grubhub/hooks/useRestaurantInformationDetail'
// import useGrubhubValidToken from '@/lib/food-grubhub/hooks/useGrubhubValidToken'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import HeadTags from '@/lib/head/HeadTags'
import {FoodGrubhubDetailRoute, FoodGrubhubMenuItemDetailRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import useParam from '@/utils/router/useParam'
import CategoryItemPrice from '@/views/FoodGrubhubRestaurantDetail/components/CategoryItemPrice'

import CustomizeUI from './CustomizeUI'
import style from './FoodGrubhubMenuItemDetail.module.scss'

type FlatSelectionMap = Record<string, number>

const DESCRIPTION_DEFAULT = 'Not available'

const ImageDefaultLoader = ({value}: {value: 'store' | 'category'}) => {
  return (
    <div className={style.StoreImageDefaultContainer}>
      {value === 'store' ? <StoreDefault /> : <CategoryDefault />}
    </div>
  )
}

export default function FoodGrubhubMenuItemDetail() {
  const {mutate: mutateFoodGrubhubCart} = useFoodGrubhubCartMutation()
  const [counter, setCounter] = useState(1)
  const [noteInput, setNoteInput] = useState('')
  const [storageUserLocation] = useGrubhubUserAddress()
  const [selectionMap, setSelectionMap] = useState<Record<string, number>>({})
  const [restaurantId] = useParam(FoodGrubhubMenuItemDetailRoute.id, 'restaurantId')
  const [menuItemId] = useParam(FoodGrubhubMenuItemDetailRoute.id, 'menuItemId')
  const [errorIds, setErrorIds] = useState<string[]>([])

  const timestampRef = useRef(Date.now())
  const descriptionElement = useRef<HTMLDivElement>(null)
  const ref = useRef<ShowMoreRef>(null)

  const toggleLines: ShowMoreToggleLinesFn = e => {
    ref.current?.toggleLines(e)
  }

  // const storageToken = useGrubhubValidToken()

  const {data: authData} = useGrubhubGetAuth()
  const {data: authResponse} = useGrubhubGetAuth()

  const menuItemDetailQuery = useMemo(
    () => ({
      restaurantId,
      menuItemId,
      time: timestampRef.current,
      hideUnavailableMenuItems: true,
      orderType: 'standard',
      locationMode: 'DELIVERY',
      version: 4,
      includeWeightedItemsData: true,
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
    }),
    [restaurantId, menuItemId, storageUserLocation.longitude, storageUserLocation.latitude],
  )

  const {
    data: menuItemDetailData,
    isLoading: isMenuItemDetailLoading,
    isError: isMenuItemDetailLoadingError,
  } = useGrubhubMenuItemDetail(menuItemDetailQuery, authData?.session_handle.access_token ?? '')

  const restaurantDetailQuery = {
    restaurantId,
    hideChoiceCategories: true,
    version: 4,
    variationId: 'rtpFreeItems',
    orderType: '',
    hideUnavailableMenuItems: false,
    hideMenuItems: false,
    locationMode: 'delivery',
    location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
  }

  const {data: restaurantDetail} = useRestaurantInformationDetail(
    restaurantDetailQuery,
    authResponse?.session_handle.access_token ?? '',
  )

  const sortCustomizations = useCallback(
    (customizations: ChoiceCategoryList[] = []): ChoiceCategoryList[] => {
      return customizations
        .map(cust => ({
          ...cust,
          options: cust.choice_option_list?.map(opt => ({
            ...opt,
            customizations: sortCustomizations(opt.choice_category_list),
          })),
        }))
        .sort((a, b) => {
          const aIsRequired = (a.min_choice_options ?? 0) > 0 ? 0 : 1
          const bIsRequired = (b.min_choice_options ?? 0) > 0 ? 0 : 1
          return aIsRequired - bIsRequired
        })
    },
    [],
  )

  const sortedCustomizations = useMemo(
    () => sortCustomizations(menuItemDetailData?.choice_category_list ?? []),
    [menuItemDetailData?.choice_category_list, sortCustomizations],
  )

  // const getInitialSelections = useCallback(
  //   (
  //     customizations: ChoiceCategoryList[],
  //     selectionMap: Record<string, number> = {},
  //   ): Record<string, number> => {
  //     for (const customization of customizations) {
  //       const requiredQty = (customization.quantity_settings?.minimum_units ?? 0) > 0 ? customization.quantity_settings?.minimum_units : customization.min_choice_options

  //       // const requiredQty = (customization.min_choice_options ?? 0) > 0 ? customization.max_choice_options : 0

  //       const options: ChoiceOptionList[] = customization.choice_option_list ?? []

  //       const sortedOptions = [...options].sort(
  //         (a, b) => (a.price?.amount ?? 0) - (b.price?.amount ?? 0),
  //       )

  //       for (let i = 0; i < (requiredQty ?? 0) && i < sortedOptions.length; i++) {
  //         const option = sortedOptions[i]
  //         if (option?.id !== undefined && option.id !== null) {
  //           selectionMap[String(option.id)] = 1
  //         }

  //         if (option?.choice_category_list?.length) {
  //           getInitialSelections(option.choice_category_list, selectionMap)
  //         }
  //       }
  //     }

  //     return selectionMap
  //   },
  //   [],
  // )

  const getInitialSelections = useCallback(
    (
      customizations: ChoiceCategoryList[],
      selectionMap: Record<string, number> = {},
    ): Record<string, number> => {
      for (const customization of customizations) {
        const minItems = customization.min_choice_options ?? 0
        const maxItems =
          customization.max_choice_options ?? customization.choice_option_list?.length ?? 0

        const requiredQty = customization.quantity_settings?.minimum_units ?? 0
        const maxTotalQty = customization.quantity_settings?.maximum_units ?? requiredQty

        const options: ChoiceOptionList[] = customization.choice_option_list ?? []

        if (minItems === 0 && requiredQty === 0) {
          continue
        }

        const sortedOptions = [...options].sort(
          (a, b) => (a.price?.amount ?? 0) - (b.price?.amount ?? 0),
        )

        const optionsToPick = Math.min(minItems, sortedOptions.length)

        let remainingQty = requiredQty

        const selectedOptions = sortedOptions.slice(0, optionsToPick)

        for (const option of selectedOptions) {
          if (!option.id) continue

          const optionLimit = option.quantity_settings?.maximum_units ?? maxTotalQty
          // const qtyForThisOption = Math.min(remainingQty > 0 ? remainingQty : 1, optionLimit);
          const qtyForThisOption = remainingQty > 0 ? Math.min(remainingQty, optionLimit) : 1

          if (qtyForThisOption > 0) {
            selectionMap[String(option.id)] = qtyForThisOption

            if (option.choice_category_list?.length) {
              getInitialSelections(option.choice_category_list, selectionMap)
            }

            remainingQty -= qtyForThisOption
          }
        }

        if (remainingQty > 0 && optionsToPick < maxItems) {
          for (let i = optionsToPick; i < sortedOptions.length && i < maxItems; i++) {
            const option = sortedOptions[i]
            if (!option?.id) continue

            const optionLimit = option.quantity_settings?.maximum_units ?? maxTotalQty
            const qtyForThisOption = Math.min(remainingQty, optionLimit)

            if (qtyForThisOption > 0) {
              selectionMap[String(option.id)] = qtyForThisOption

              if (option.choice_category_list?.length) {
                getInitialSelections(option.choice_category_list, selectionMap)
              }

              remainingQty -= qtyForThisOption
              if (remainingQty <= 0) break
            }
          }
        }
      }

      return selectionMap
    },
    [],
  )

  useEffect(() => {
    if (restaurantId !== '' && menuItemId !== '') {
      const initial = getInitialSelections(menuItemDetailData?.choice_category_list ?? [])
      setSelectionMap(initial)
    }
  }, [restaurantId, menuItemId, menuItemDetailData?.choice_category_list, getInitialSelections])

  const maxItemQuantityAvailable = menuItemDetailData?.maximum_order_quantity ?? 99

  const handleCounterAdding = () => {
    if (counter === maxItemQuantityAvailable) {
      return
    }
    setCounter(counter + 1)
  }

  const handleCounterRemoving = () => {
    if (counter === 0) {
      return
    }
    setCounter(counter - 1)
  }

  useEffect(() => {
    announce(`You have selected ${counter} ${menuItemDetailData?.name}${counter > 1 ? 's' : ''}`)
  }, [counter, menuItemDetailData?.name])

  const calculateTotalPrice = (
    customization: ChoiceCategoryList,
    selectionMap: Record<string, number>,
  ): Big => {
    let total = new Big(0)

    customization.choice_option_list?.forEach(option => {
      const qty = selectionMap[option.id ?? ''] ?? 0

      if (qty > 0) {
        total = total.plus(new Big(option.price?.amount ?? 0).times(qty))

        option.choice_category_list?.forEach(nested => {
          total = total.plus(calculateTotalPrice(nested, selectionMap))
        })
      }
    })

    return total
  }

  const getTotalPrice = (
    basePrice: number,
    customizations: ChoiceCategoryList[],
    selectionMap: Record<string, number>,
  ): string => {
    const baseProductPriceWithQuantity = basePrice

    let total = new Big(baseProductPriceWithQuantity)

    customizations.forEach(customization => {
      total = total.plus(calculateTotalPrice(customization, selectionMap))
    })

    return total.times(counter).div(100).toFixed(2)
  }

  const total = getTotalPrice(
    menuItemDetailData?.price?.amount ?? 0,
    menuItemDetailData?.choice_category_list ?? [],
    selectionMap,
  )

  const validateCustomizationWithErrors = (
    customization: ChoiceCategoryList,
    selectionMap: FlatSelectionMap,
    ids: string[],
  ): string[] => {
    const errors: string[] = []
    const selectedQty = customization.choice_option_list?.reduce((sum, option) => {
      const qty = selectionMap[option.id ?? ''] ?? 0
      return sum + qty
    }, 0)

    if ((selectedQty ?? 0) < (customization.quantity_settings?.minimum_units ?? 0)) {
      errors.push(
        `You must choose at least ${customization.quantity_settings?.minimum_units} unit${(customization.quantity_settings?.minimum_units ?? 0) > 1 ? `s` : ``} for "${customization.name}"`,
      )
      ids.push(customization.id ?? '')
    }

    if ((selectedQty ?? 0) < (customization.min_choice_options ?? 0)) {
      errors.push(
        `You must choose at least ${customization.min_choice_options} option${(customization.min_choice_options ?? 0) > 1 ? `s` : ``} for "${customization.name}"`,
      )
      ids.push(customization.id ?? '')
    }

    for (const option of customization.choice_option_list ?? []) {
      const qty = selectionMap[option.id ?? ''] ?? 0

      if (qty > 0 && option.choice_category_list?.length) {
        for (const nested of option.choice_category_list) {
          const nestedErrors = validateCustomizationWithErrors(nested, selectionMap, ids)
          errors.push(...nestedErrors)
        }
      }
    }

    return errors
  }

  const validateAllCustomizationsWithErrors = (
    customizations: ChoiceCategoryList[],
    selectionMap: FlatSelectionMap,
    ids: string[],
  ): string[] => {
    return customizations.flatMap(c => validateCustomizationWithErrors(c, selectionMap, ids))
  }

  const buildOptionsFromSelectionMap = (
    customizations: ChoiceCategoryList[],
    selectionMap: Record<string, number>,
  ) => {
    const options: FoodGrubhubCartOption[] = []

    for (const customization of customizations) {
      for (const option of customization.choice_option_list ?? []) {
        const optionId = option.id ?? ''
        const quantity = selectionMap[optionId] ?? 0

        if (quantity > 0) {
          const childOptions = option.choice_category_list?.length
            ? buildOptionsFromSelectionMap(option.choice_category_list, selectionMap)
            : []

          options.push({
            id: optionId,
            quantity,
            childOptions,
          })
        }
      }
    }

    return options
  }

  //Prefix
  // console.log(menuItemDetailData)
  const menuItemImageLength = menuItemDetailData?.media_images?.length ?? 0
  const menuItemImagePathBuilder = `${menuItemDetailData?.media_image?.base_url}${menuItemDetailData?.media_image?.public_id}.${menuItemDetailData?.media_image?.format}`

  const handleSubmit = () => {
    const ids: string[] = []
    const errors = validateAllCustomizationsWithErrors(
      menuItemDetailData?.choice_category_list ?? [],
      selectionMap,
      ids,
    )
    setErrorIds(ids)

    if (errors.length > 0) {
      errors.forEach(error => {
        toastErrorMessage(error)
      })
      return
    }

    console.log('Selection map:', selectionMap)

    const options = buildOptionsFromSelectionMap(
      menuItemDetailData?.choice_category_list ?? [],
      selectionMap,
    )

    console.log('Built options:', options)

    mutateFoodGrubhubCart({
      action: 'Add',
      data: {
        item: {
          restaurantId,
          menuItemId,
          options,
          specialInstructions: noteInput,
          moreInfo: {
            restaurantName: restaurantDetail?.restaurant.name ?? '',
            restaurantImgUrl: restaurantDetail?.restaurant.logo ?? '',
            restaurantAddress: [
              restaurantDetail?.restaurant.address?.street_address,
              restaurantDetail?.restaurant.address?.locality,
              restaurantDetail?.restaurant.address?.postal_code,
            ]
              .filter(Boolean)
              .join(', '),
            restaurantPhone: restaurantDetail?.restaurant.phone_number_for_delivery ?? '',
            menuItemImgUrl: `${menuItemDetailData?.media_image?.base_url}${menuItemDetailData?.media_image?.public_id}.${menuItemDetailData?.media_image?.format}`,
          },
        },
      },
    })
  }

  return (
    <div>
      <HeadTags title={menuItemDetailData?.name ?? 'Customization'} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar withSearch variant='dark' />
      <main className={style.PageContainer}>
        {isMenuItemDetailLoading ? (
          <Loading label={'Loading menu item customization page'} />
        ) : (
          <>
            {isMenuItemDetailLoadingError ? (
              <section className={style.PageLoadErrContainer}>
                <img src={notFoundDecorSvg} className={style.DecorSvg} alt='' />
                <h1 className={style.Headling}>
                  Sorry, this item isn&apos;t available at the moment.
                </h1>
                <VisuallyHidden>
                  <div role='alert'>
                    <span>Sorry, this item isn&apos;t available at the moment.</span>
                  </div>
                </VisuallyHidden>
                <PrimaryButton
                  onPress={() => {
                    globalThis.history.back()
                  }}
                >
                  Go back
                </PrimaryButton>
              </section>
            ) : (
              <>
                <section className={style.PageHeader}>
                  <div className={style.PageHeaderContent}>
                    <Link
                      to={FoodGrubhubDetailRoute.fullPath}
                      aria-label='Go back to store detail page'
                      params={{
                        restaurantId,
                      }}
                      className={style.ButtonBack}
                    >
                      <ChevronLeft className={style.ButtonBackIcon} />
                      <p className={style.ButtonBackTitleMobile}>Go back</p>
                    </Link>
                    <div className={style.HeaderImageContainer}>
                      {menuItemImageLength ? (
                        <img
                          src={menuItemImagePathBuilder}
                          alt=''
                          loading='lazy'
                          className={style.HeaderImage}
                        />
                      ) : (
                        <ImageDefaultLoader value='category' />
                      )}
                    </div>
                    <div className={style.HeaderInfoContainer}>
                      <div className={style.HeaderInfoContent}>
                        <h1 className={style.Heading}>{menuItemDetailData?.name}</h1>
                        <div className={style.Description}>
                          <VisuallyHidden>
                            Description:{' '}
                            {menuItemDetailData?.description?.length === 0 && DESCRIPTION_DEFAULT}
                          </VisuallyHidden>
                          <div ref={descriptionElement}>
                            <ShowMore
                              ref={ref}
                              lines={1}
                              more={
                                <button
                                  onClick={toggleLines}
                                  className={style.ReadMore}
                                  type='button'
                                >
                                  Read more
                                </button>
                              }
                              less={
                                <button
                                  onClick={toggleLines}
                                  className={style.ReadMore}
                                  type='button'
                                >
                                  Read less
                                </button>
                              }
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: menuItemDetailData?.description ?? '',
                                }}
                              />
                            </ShowMore>
                          </div>
                        </div>
                      </div>
                      <div className={style.HeaderInfoBreak} />
                      <div className={style.HeaderInfoController}>
                        <div className={style.CounterContainer}>
                          <div className={style.CounterHeading}>Quantity</div>
                          <div className={style.CounterContent}>
                            <button
                              className={style.CounterButton}
                              type='button'
                              onClick={handleCounterRemoving}
                              aria-label='Decrease item quantity by one'
                            >
                              -
                            </button>
                            <div
                              className={style.CounterValue}
                              // aria-live='polite'
                              // aria-atomic='true'
                            >
                              {counter}
                            </div>
                            <button
                              className={style.CounterButton}
                              type='button'
                              onClick={handleCounterAdding}
                              aria-label='Increase item quantity by one'
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <button
                          type='button'
                          onClick={handleSubmit}
                          className={style.CartButton}
                          disabled={counter === 0}
                        >
                          <CartPlusIcon className={style.CartIcon} />
                          <div className={style.CartValue}>
                            Add item to cart - <CategoryItemPrice price={total} />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
                <section className={style.PageContent}>
                  <div className={style.NoteContainer}>
                    <h2 className={style.NoteHeading}>Add your note here</h2>
                    <textarea
                      rows={4}
                      className={style.NoteInput}
                      placeholder=''
                      value={noteInput}
                      onChange={e => {
                        setNoteInput(e.target.value)
                      }}
                      aria-placeholder='Add your note here'
                    />
                  </div>
                  <div className={style.CategoryList} role='list'>
                    {sortedCustomizations.map(customization => (
                      <CustomizeUI
                        key={customization.id}
                        errorIds={errorIds}
                        customization={customization}
                        selectionMap={selectionMap}
                        setSelectionMap={setSelectionMap}
                      />
                    ))}
                  </div>
                </section>
                <div className={style.MobileController}>
                  <div className={style.CounterContainer}>
                    <h2 className={style.CounterHeading}>Quantity</h2>
                    <div className={style.CounterContent}>
                      <button
                        className={style.CounterButton}
                        type='button'
                        onClick={handleCounterRemoving}
                        aria-label='Descrease item quantity by one'
                      >
                        -
                      </button>
                      <div className={style.CounterValue}>{counter}</div>
                      <button
                        className={style.CounterButton}
                        type='button'
                        onClick={handleCounterAdding}
                        aria-label='Increase item quantity by one'
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    type='button'
                    disabled={counter === 0}
                    className={style.CartButton}
                    onClick={handleSubmit}
                  >
                    <CartPlusIcon className={style.CartIcon} />
                    <div className={style.CartValue}>
                      Add item to cart - <CategoryItemPrice price={total} />
                    </div>
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  )
}
